import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../../api/client";
import { useAuthStore } from "../../store/authStore";
import Spinner from "../../components/Spinner";

type Daeun = {
  index: number;
  gan: string;
  ji: string;
  pillar: string;
  gan_element: string;
  ji_element: string;
  age_start: number;
  age_end: number;
};

type DaeunResult = {
  forward: boolean;
  start_age: number;
  current_age: number;
  month_pillar: { gan: string; ji: string };
  year_gan: string;
  daeun_list: Daeun[];
  current_daeun: Daeun | null;
  ai_commentary: string | null;
};

const ELEMENT_COLOR: Record<string, string> = {
  목: "bg-green-900/30 text-green-400 border-green-500/30",
  화: "bg-red-900/30 text-red-400 border-red-500/30",
  토: "bg-yellow-900/30 text-yellow-400 border-yellow-500/30",
  금: "bg-slate-700/30 text-slate-300 border-slate-500/30",
  수: "bg-blue-900/30 text-blue-400 border-blue-500/30",
};

const ELEMENT_DOT: Record<string, string> = {
  목: "bg-green-500",
  화: "bg-red-500",
  토: "bg-yellow-500",
  금: "bg-gray-400",
  수: "bg-blue-500",
};

function ElementBadge({ element }: { element: string }) {
  const cls = ELEMENT_COLOR[element] ?? "bg-[var(--color-surface)] text-[var(--color-text-tertiary)] border-[var(--color-border-default)]";
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${cls}`}>
      {element}
    </span>
  );
}

export default function DaeunAnalysis() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [data, setData] = useState<DaeunResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [commentaryMap, setCommentaryMap] = useState<Record<number, string | null>>({});
  const [commentaryLoading, setCommentaryLoading] = useState<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    setLoading(true);
    client
      .get("/features/daeun")
      .then((r) => setData(r.data))
      .catch((e) => {
        const msg = e?.response?.data?.detail ?? "대운 정보를 불러올 수 없습니다.";
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  const handleSelectDaeun = async (index: number) => {
    if (selectedIndex === index) {
      setSelectedIndex(null);
      return;
    }
    setSelectedIndex(index);
    if (commentaryMap[index] !== undefined) return;
    setCommentaryLoading(index);
    try {
      const r = await client.get(`/features/daeun/commentary?index=${index}`);
      setCommentaryMap((prev) => ({ ...prev, [index]: r.data.commentary }));
    } catch {
      setCommentaryMap((prev) => ({ ...prev, [index]: null }));
    } finally {
      setCommentaryLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-lg mx-auto">
        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/dashboard")}
            className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">🌊 대운 분석</h1>
            <p className="text-[var(--color-text-tertiary)] text-xs">10년 주기 대운(大運) 흐름</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <div className="text-center py-20 text-[var(--color-text-tertiary)]">
            <p className="text-4xl mb-4">🌊</p>
            <p>{error}</p>
            {error.includes("사주") && (
              <button
                onClick={() => navigate("/profile")}
                className="mt-4 px-4 py-2 bg-[var(--color-accent)] text-[var(--color-primary)] rounded-xl text-sm font-bold"
              >
                사주 정보 입력하기
              </button>
            )}
          </div>
        ) : data ? (
          <>
            {/* 요약 카드 */}
            <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs text-[var(--color-text-tertiary)]">대운 방향</p>
                  <p className="text-base font-bold text-[var(--color-text-primary)]">
                    {data.forward ? "순행(順行)" : "역행(逆行)"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[var(--color-text-tertiary)]">대운 기수(起數)</p>
                  <p className="text-base font-bold text-[var(--color-accent)]">{data.start_age}세 시작</p>
                </div>
              </div>
              <div className="border-t border-dashed border-[var(--color-border-default)] pt-3">
                <p className="text-xs text-[var(--color-text-tertiary)] mb-1">원국 월주(月柱)</p>
                <p className="text-sm font-medium text-[var(--color-text-primary)]">
                  {data.month_pillar.gan}{data.month_pillar.ji}월
                </p>
              </div>
            </div>

            {/* 현재 대운 AI 해설 */}
            {data.current_daeun && (
              <div className="bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/20 rounded-2xl p-5 mb-4">
                <p className="text-xs font-bold text-[var(--color-accent)] mb-2">
                  ✨ 현재 대운 — {data.current_daeun.pillar}운
                  ({data.current_daeun.age_start}~{data.current_daeun.age_end}세)
                </p>
                {data.ai_commentary ? (
                  <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">{data.ai_commentary}</p>
                ) : (
                  <p className="text-sm text-[var(--color-text-tertiary)]">
                    현재 나이 {data.current_age}세, {data.current_daeun.gan_element}과{" "}
                    {data.current_daeun.ji_element}의 기운이 교차하는 시기입니다.
                  </p>
                )}
              </div>
            )}

            {/* 타임라인 */}
            <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
              <h2 className="text-sm font-bold text-[var(--color-text-primary)] mb-4">📅 대운 타임라인</h2>
              <div className="relative">
                {/* 세로선 */}
                <div className="absolute left-4 top-0 bottom-0 w-px bg-[var(--color-border-default)]" />

                <div className="space-y-4">
                  {data.daeun_list.map((d) => {
                    const isCurrent = data.current_daeun?.index === d.index;
                    const isPast = d.age_end < data.current_age;
                    const isSelected = selectedIndex === d.index;
                    const dotColor = ELEMENT_DOT[d.gan_element] ?? "bg-gray-400";
                    const commentary = commentaryMap[d.index];
                    const isLoadingThis = commentaryLoading === d.index;

                    return (
                      <div key={d.index} className="relative flex gap-4">
                        {/* 도트 */}
                        <div className={`
                          relative z-10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2
                          ${isCurrent
                            ? "bg-[var(--color-accent)] border-[var(--color-accent)] shadow-md"
                            : isPast
                              ? "bg-[var(--color-border-default)] border-[var(--color-border-default)]"
                              : "bg-[var(--color-bg)] border-[var(--color-border-default)]"
                          }
                        `}>
                          {isCurrent ? (
                            <span className="text-xs font-bold text-[var(--color-primary)]">NOW</span>
                          ) : (
                            <div className={`w-2.5 h-2.5 rounded-full ${isPast ? "bg-[var(--color-text-disabled)]" : dotColor}`} />
                          )}
                        </div>

                        {/* 내용 */}
                        <div
                          className={`
                            flex-1 rounded-xl border cursor-pointer transition-all
                            ${isCurrent
                              ? "bg-[var(--color-accent)]/10 border-[var(--color-accent)]/30"
                              : isPast
                                ? "bg-[var(--color-bg)] border-[var(--color-border-default)] opacity-60"
                                : "bg-[var(--color-bg)] border-[var(--color-border-default)]"
                            }
                            ${isSelected ? "ring-2 ring-[var(--color-accent)]/40" : ""}
                          `}
                          onClick={() => handleSelectDaeun(d.index)}
                        >
                          <div className="p-3">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <span className={`text-base font-bold ${isCurrent ? "text-[var(--color-accent)]" : isPast ? "text-[var(--color-text-disabled)]" : "text-[var(--color-text-primary)]"}`}>
                                  {d.pillar}운
                                </span>
                                {isCurrent && (
                                  <span className="text-[10px] bg-[var(--color-accent)] text-[var(--color-primary)] px-1.5 py-0.5 rounded font-bold">
                                    현재
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-[var(--color-text-tertiary)]">
                                  {d.age_start}~{d.age_end}세
                                </span>
                                <svg
                                  className={`w-3.5 h-3.5 text-[var(--color-text-disabled)] transition-transform ${isSelected ? "rotate-180" : ""}`}
                                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>
                            </div>
                            <div className="flex gap-1.5">
                              <div className="flex items-center gap-1">
                                <span className="text-[11px] text-[var(--color-text-tertiary)]">천간</span>
                                <span className="text-[11px] font-medium text-[var(--color-text-secondary)]">{d.gan}</span>
                                <ElementBadge element={d.gan_element} />
                              </div>
                              <span className="text-[var(--color-border-default)]">·</span>
                              <div className="flex items-center gap-1">
                                <span className="text-[11px] text-[var(--color-text-tertiary)]">지지</span>
                                <span className="text-[11px] font-medium text-[var(--color-text-secondary)]">{d.ji}</span>
                                <ElementBadge element={d.ji_element} />
                              </div>
                            </div>
                          </div>

                          {/* 확장 해설 패널 */}
                          {isSelected && (
                            <div className="border-t border-dashed border-[var(--color-border-default)] px-3 py-3">
                              {isLoadingThis ? (
                                <div className="flex items-center gap-2 text-[var(--color-text-disabled)] text-xs">
                                  <Spinner size="sm" />
                                  <span>럭키가 꺼내오는 중이에요</span>
                                </div>
                              ) : commentary ? (
                                <p className="text-xs text-[var(--color-text-secondary)] leading-relaxed">{commentary}</p>
                              ) : (
                                <p className="text-xs text-[var(--color-text-disabled)]">해설을 불러올 수 없습니다.</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* 상담 버튼 */}
            <div className="mt-4">
              <button
                onClick={() =>
                  navigate("/counseling", {
                    state: { autoStart: true, category: "general", mode: "chat" },
                  })
                }
                className="w-full py-3 bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-primary)] rounded-xl text-sm font-bold transition-opacity"
              >
                💬 대운에 대해 더 상담하기
              </button>
            </div>

            {/* 주의 */}
            <p className="text-center text-[var(--color-text-disabled)] text-[11px] mt-3">
              * 절기 근사값 기반 계산입니다. 정밀한 만세력과 다를 수 있습니다.
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}
